import { toRestoredReportingChartContext } from 'src/agent/reporting/chartQuestion.types'
import type { ReportingDraftState } from 'src/agent/reporting/conversation/reportingDraft.reducer'
import type {
  ReportingArtifact,
  ReportingChatMessage,
} from 'src/agent/reporting/reportingPresentation.types'
import type {
  DraftDisplayProjection,
  ReportingMessage,
  ReportingRunProjection,
} from 'src/queries/reportingAgent/reportingAgent.models'

import type { ReportingAgentConversationStatus } from './reportingConversation.types'

type ReportingArtifactProjection = Pick<
  DraftDisplayProjection,
  | 'artifact_id'
  | 'chart_spec'
  | 'dataset'
  | 'title'
  | 'live_report_candidate_id'
>

export const toReportingPresentationArtifact = (
  value: ReportingArtifactProjection | null | undefined,
): ReportingArtifact | null =>
  value
    ? {
        id: value.artifact_id,
        title: value.title,
        liveReportCandidateId: value.live_report_candidate_id,
        chartSpec: value.chart_spec,
        dataset: value.dataset,
      }
    : null

export const matchingArtifactRunId = (
  run: Pick<ReportingRunProjection, 'id' | 'artifact_id'> | null | undefined,
  artifact: Pick<DraftDisplayProjection, 'artifact_id'> | null | undefined,
): string | undefined =>
  run?.artifact_id && artifact?.artifact_id === run.artifact_id
    ? run.id
    : undefined

export const reportingStatusMessage = (
  status: ReportingAgentConversationStatus,
): string => {
  switch (status) {
    case 'submitting':
      return 'Creating report…'
    case 'running':
      return 'Generating report…'
    case 'refining':
      return 'Refining report…'
    case 'ready':
      return 'Report ready'
    default:
      return ''
  }
}

export const toReportingChatMessages = (
  accessDenied: boolean,
  messages: readonly ReportingMessage[],
): readonly ReportingChatMessage[] =>
  accessDenied
    ? []
    : messages
        .slice()
        .reverse()
        .map((message) => ({
          id: message.id,
          role: message.role,
          content: message.content,
          runId: message.run_id,
          createdAt: message.created_at,
        }))

export const latestRestoredChartContext = (
  messages: readonly ReportingMessage[],
) =>
  toRestoredReportingChartContext(
    messages
      .slice()
      .sort((left, right) => right.created_at.localeCompare(left.created_at))
      .find((message) => message.chart_context)?.chart_context,
  )

export const reportingConversationStatus = (
  accessDenied: boolean,
  queryFailed: boolean,
  draftStatus: ReportingDraftState['status'],
): ReportingAgentConversationStatus => {
  if (accessDenied || queryFailed) return 'error'
  switch (draftStatus) {
    case 'submitting':
    case 'running':
    case 'refining':
      return draftStatus
    case 'ready':
      return 'ready'
    case 'error':
      return 'error'
    default:
      return 'idle'
  }
}
